import React from 'react';
import LineIcon from '@mui/icons-material/TableRows';
import { Button, Grid, Stack, Typography } from '@mui/material';
import { useParams } from 'react-router';

import { isLoggedInUserRoot } from '@paragon/login-tools';
import { sortBy } from '@paragon/utilities';

import { showCreateStationDialog, showEditLineDialog } from '../../actions/dialogActions';
import { deleteLine } from '../../actions/supervisorActions';
import { useLineBreadcrumbs } from '../../selectors/breadcrumbsSelector';
import { getHolisticViewModel, LineViewModel } from '../../selectors/holisticViewModelSelector';
import { useAppDispatch, useAppSelector } from '../hooks';
import { Breadcrumbs } from '../interface/Breadcrumbs';
import { StationCard } from '../interface/cards/StationCard';
import { ResponsiveButtonBar } from '../interface/ResponsiveButtonBar';
import { ErrorPage } from './ErrorPage';

export function LinePage() {
  const dispatch = useAppDispatch();
  const { lineGuid } = useParams();
  const breadcrumbs = useLineBreadcrumbs(lineGuid!);

  const isRootUser = useAppSelector(isLoggedInUserRoot);

  const viewModel = useAppSelector(getHolisticViewModel);
  if (viewModel === 'loading' || viewModel === 'error') return null;

  const lineViewModel = viewModel.flatView[lineGuid!] as LineViewModel;
  if (lineViewModel == null) return <ErrorPage body="That line can not be found." />;

  let stations = Object.entries(lineViewModel.children.stations);
  stations = sortBy(stations, [
    ([_, insertedModel]) => insertedModel.yIndex,
    ([_, insertedModel]) => insertedModel.source.name,
  ]);

  const stationCards = stations.map(([guid, model]) => (
    <Grid size={12} key={guid}>
      <StationCard lineGuid={lineGuid} station={model.source} />
    </Grid>
  ));

  return (
    <Stack>
      {breadcrumbs.length > 1 && <Breadcrumbs crumbs={breadcrumbs} />}

      <Stack direction="row" sx={{ alignItems: 'center' }}>
        <Typography variant="h3" gutterBottom sx={{ marginRight: 'auto' }}>
          <Stack direction="row" sx={{ alignItems: 'center' }}>
            <LineIcon fontSize="inherit" sx={{ marginRight: 1 }} />
            {lineViewModel.source.name}
          </Stack>
        </Typography>

        {isRootUser && (
          <ResponsiveButtonBar
            breakpoint="md"
            actions={[
              ['Edit', () => void dispatch(showEditLineDialog(lineGuid!))],
              ['Delete', () => void dispatch(deleteLine(lineGuid!))],
            ]}
          />
        )}
      </Stack>

      {isRootUser && (
        <Button
          fullWidth
          variant="outlined"
          sx={{ marginBottom: 3 }}
          onClick={() => {
            dispatch(showCreateStationDialog(lineGuid!));
          }}
        >
          Add new station
        </Button>
      )}

      <Grid container spacing={1}>
        {stationCards}
      </Grid>
    </Stack>
  );
}
